import { VStack } from "@chakra-ui/react";
import {
  AddCircleOutlined as AddCircleOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import {
  FieldArray,
  FieldArrayRenderProps,
  Form,
  Formik,
  useFormikContext,
} from "formik";
import { FC, useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useParams } from "react-router-dom";
import * as yup from "yup";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { PageKind } from "~/src/__generated__/schema";
import { CopyModal_Query } from "~/src/__relay_artifacts__/CopyModal_Query.graphql";
import { SymbolOutlineButton } from "~/src/components/common/Button";
import { FormControl } from "~/src/components/common/forms/FormControl";
import { FormikFormButtons } from "~/src/components/common/forms/FormikFormButtons";
import { FormikSelectField } from "~/src/components/common/forms/FormikSelectField";
import { IconButton } from "~/src/components/common/IconButton";
import { Modal } from "~/src/components/common/Modal";
import { HorizontalSeparator } from "~/src/components/common/Separator";

export type TargetFunnel = {
  funnelId: string;
  pageKind: PageKind;
  pageId: string;
};

export type FormTargetFunnel = TargetFunnel & {
  id: string; // Unique ID for React key, not submitted
};

export type CopyModalInitialValues = {
  funnelId: string;
  pageKind: PageKind;
  pageId: string;
};

export type FormValues = {
  targetFunnels: FormTargetFunnel[];
};

export type Props = {
  initialValues: CopyModalInitialValues;
  onSubmit: (pageId: string) => Promise<void> | void;
  isOpen: boolean;
  onClose: () => void;
};

const query = graphql`
  query CopyModal_Query($siteSlug: String!) {
    site(slug: $siteSlug) {
      pages {
        edges {
          node {
            id
            name
            kind
            sortNumber
            funnel {
              id
              name
            }
          }
        }
      }
      funnels {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  }
`;

const validationSchema = yup.object({
  targetFunnels: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        funnelId: yup.string().nullable(),
        pageKind: yup.string().nullable(),
        pageId: yup.string().required("ページを選択してください"),
      })
    )
    .min(1, "少なくとも1つの対象ファネルが必要です")
    .required(),
});

type TargetFunnelFieldsProps = {
  index: number;
  arrayHelpers: FieldArrayRenderProps;
  site: CopyModal_Query["response"]["site"];
};

const TargetFunnelFields: FC<TargetFunnelFieldsProps> = ({
  index,
  arrayHelpers,
  site,
}) => {
  const { values } = useFormikContext<FormValues>();
  const targetFunnel = values.targetFunnels[index];

  const funnelOptions = useMemo(
    () => [
      ...(site.funnels.edges || []).map((edge) => {
        const node = edge?.node;
        if (!node) throw new Error("assertion failed");
        return { label: node.name, value: node.id };
      }),
    ],
    [site.funnels.edges]
  );

  const pageKindOptions = useMemo(
    () => [{ label: "すべて", value: null }, ...GraphQLEnums.PageKind],
    []
  );

  const pageOptions = useMemo(() => {
    if (!targetFunnel) return [];
    return [
      ...(site.pages.edges || []).map((edge) => {
        const node = edge?.node;
        if (!node) throw new Error("assertion failed");
        return {
          label:
            node.kind === "FORM"
              ? `${node.name}(${node.sortNumber - 1})[${node.funnel.name}]`
              : `${node.name}(${node.funnel.name})`,
          value: node.id,
          funnelId: node.funnel.id,
          pageKind: node.kind,
        };
      }),
    ]
      .filter(
        (v) => !targetFunnel.funnelId || v.funnelId === targetFunnel.funnelId
      )
      .filter(
        (v) => !targetFunnel.pageKind || v.pageKind === targetFunnel.pageKind
      );
  }, [site.pages.edges, targetFunnel]);

  const handleDelete = useCallback(() => {
    arrayHelpers.remove(index);
  }, [arrayHelpers, index]);

  return (
    <VStack spacing="16px" width="100%">
      {index > 0 && <HorizontalSeparator />}
      <VStack spacing="16px" width="100%" align="flex-start">
        {index > 0 && (
          <VStack width="100%" align="flex-end">
            <IconButton
              size="24px"
              onClick={handleDelete}
              icon={<DeleteOutlineIcon />}
            />
          </VStack>
        )}
        <FormControl label="ファネル">
          <FormikSelectField
            name={`targetFunnels.${index}.funnelId`}
            options={funnelOptions}
          />
        </FormControl>
        <FormControl label="ページタイプ">
          <FormikSelectField
            name={`targetFunnels.${index}.pageKind`}
            options={pageKindOptions}
          />
        </FormControl>
        <FormControl label="ページ">
          <FormikSelectField
            name={`targetFunnels.${index}.pageId`}
            options={pageOptions}
          />
        </FormControl>
      </VStack>
    </VStack>
  );
};

export const CopyModal: FC<Props> = ({
  isOpen,
  onClose,
  initialValues,
  onSubmit,
}) => {
  const { siteSlug = "" } = useParams();

  const { site } = useLazyLoadQuery<CopyModal_Query>(query, {
    siteSlug,
  });

  const generateId = useCallback(() => {
    return `target-funnel-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  }, []);

  const formInitialValues: FormValues = useMemo(
    () => ({
      targetFunnels: [
        {
          id: generateId(),
          funnelId: initialValues.funnelId,
          pageKind: initialValues.pageKind,
          pageId: initialValues.pageId,
        },
      ],
    }),
    [initialValues, generateId]
  );

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      // Strip out the id field and execute all copy operations in parallel
      await Promise.all(
        values.targetFunnels.map((formTargetFunnel) => {
          const { id, ...targetFunnel } = formTargetFunnel;
          return onSubmit(targetFunnel.pageId);
        })
      );
      onClose();
    },
    [onClose, onSubmit]
  );

  const handleAddTargetFunnel = useCallback(
    (arrayHelpers: FieldArrayRenderProps) => {
      arrayHelpers.push({
        id: generateId(),
        funnelId: "",
        pageKind: null,
        pageId: "",
      });
    },
    [generateId]
  );

  return (
    <Modal title="コピー" isOpen={isOpen} onClose={onClose}>
      <Formik<FormValues>
        enableReinitialize
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        validateOnMount={false}
        validateOnChange={false}
        onSubmit={handleSubmit}
      >
        {/* eslint-disable-next-line react/jsx-no-bind */}
        {({ values }) => (
          <Form>
            <VStack spacing="16px">
              <FieldArray
                name="targetFunnels"
                // eslint-disable-next-line react/jsx-no-bind
                render={(arrayHelpers: FieldArrayRenderProps) => (
                  <>
                    {values.targetFunnels.map((funnel, index) => (
                      <TargetFunnelFields
                        key={funnel.id}
                        index={index}
                        arrayHelpers={arrayHelpers}
                        site={site}
                      />
                    ))}
                    <VStack alignSelf={"flex-start"} alignItems={"flex-start"}>
                      <SymbolOutlineButton
                        icon={<AddCircleOutlinedIcon />}
                        // eslint-disable-next-line react/jsx-no-bind
                        onClick={() => handleAddTargetFunnel(arrayHelpers)}
                      >
                        対象ファネルを追加
                      </SymbolOutlineButton>
                    </VStack>
                  </>
                )}
              />
            </VStack>
            <FormikFormButtons onCancelClick={onClose} />
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
